import { useState, type ComponentType, type SVGProps } from "react";
import { ChevronLeft, ChevronRight, Link2, Loader2, Users } from "lucide-react";

import { ContributorPicker } from "@/components/issues/contributor-picker/contributor-picker";
import type { PickerIssue } from "@/components/issues/contributor-picker/pickable-members";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { cn } from "@/lib/utils";

interface AddContributorSheetProps {
  projectId: string;
  issue: PickerIssue;
  /** For "Pick from people already on {name}." Absent while it loads. */
  projectName: string | undefined;
  currentUserId: string | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** A centred dialog on desktop, a bottom sheet on a phone. */
  presentation: "drawer" | "dialog";
  /** Send a link. Resolves true once a link exists, and the sheet closes. */
  onSendLink: () => Promise<boolean>;
  /** A link is being made: Send a link spins and ignores taps. */
  sending: boolean;
}

/**
 * The two ways onto an issue for people who can see every issue: someone
 * already on the project, or a link. See [[add-contributor-sheet.spec]].
 */
export function AddContributorSheet({
  open,
  onOpenChange,
  presentation,
  ...rest
}: AddContributorSheetProps) {
  // Keyed on open, so every opening starts at the two choices.
  const body = (
    <SheetBody
      key={open ? "open" : "closed"}
      presentation={presentation}
      onClose={() => onOpenChange(false)}
      {...rest}
    />
  );

  if (presentation === "dialog") {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent bare aria-describedby={undefined} className="sm:max-w-md">
          {body}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[90vh]">{body}</DrawerContent>
    </Drawer>
  );
}

type SheetBodyProps = Omit<AddContributorSheetProps, "open" | "onOpenChange"> & {
  onClose: () => void;
};

function SheetBody({
  projectId,
  issue,
  projectName,
  currentUserId,
  presentation,
  onSendLink,
  sending,
  onClose,
}: SheetBodyProps) {
  const [step, setStep] = useState<"choose" | "people">("choose");
  // The title must belong to whichever primitive hosts the body: each wires
  // its own title to its own accessible name.
  const Header = presentation === "dialog" ? DialogHeader : DrawerHeader;
  const Title = presentation === "dialog" ? DialogTitle : DrawerTitle;
  const headerClassName = cn("text-left", presentation === "dialog" && "p-4 pr-12");

  if (step === "people") {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <Header
          className={cn(headerClassName, "flex flex-row items-center gap-1 pl-2")}
        >
          <button
            type="button"
            aria-label="Back"
            onClick={() => setStep("choose")}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-accent"
          >
            <ChevronLeft className="h-5 w-5" aria-hidden="true" />
          </button>
          <Title className="text-base">Someone on this project</Title>
        </Header>
        <ContributorPicker
          projectId={projectId}
          issue={issue}
          currentUserId={currentUserId}
          onAdded={onClose}
        />
      </div>
    );
  }

  const sendLink = async () => {
    if (sending) return;
    if (await onSendLink()) onClose();
  };

  return (
    <>
      <Header className={headerClassName}>
        <Title>Add contributor</Title>
      </Header>
      <div className="flex flex-col gap-2.5 px-4">
        <Choice
          icon={Users}
          title="Someone on this project"
          line={`Pick from people already on ${projectName ?? "this project"}.`}
          onClick={() => setStep("people")}
        />
        <Choice
          icon={Link2}
          title="Send a link"
          line="Share it on WhatsApp. Works for 7 days."
          onClick={() => void sendLink()}
          busy={sending}
        />
      </div>
      <div className="pb-sheet-actions p-4">
        <Button variant="outline" className="h-[46px] w-full" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </>
  );
}

function Choice({
  icon: Icon,
  title,
  line,
  onClick,
  busy = false,
}: {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  title: string;
  line: string;
  onClick: () => void;
  busy?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-busy={busy}
      className="flex w-full items-center gap-3.5 rounded-xl border border-border bg-card py-3.5 pr-2 pl-3.5 text-left transition-colors hover:bg-accent"
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary text-foreground">
        <Icon className="h-[19px] w-[19px]" aria-hidden="true" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-[15px] font-semibold">{title}</span>
        <span className="text-[13px] leading-snug text-muted-foreground">
          {line}
        </span>
      </span>
      <span className="flex w-7 shrink-0 justify-center text-muted-foreground">
        {busy ? (
          <Loader2 className="h-[18px] w-[18px] animate-spin" aria-hidden="true" />
        ) : (
          <ChevronRight className="h-[18px] w-[18px]" aria-hidden="true" />
        )}
      </span>
    </button>
  );
}
